import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { login, clearError } from '../store/slices/authSlice';
import api from '../api';

function SuperAdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'superadmin') {
      navigate('/super-admin/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    const result = await dispatch(login({ email, password }));

    if (login.fulfilled.match(result) && result.payload.user?.role === 'superadmin') {
      navigate('/super-admin/dashboard');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordMessage('');

    try {
      const res = await api.post('/auth/forgot-password', { email: forgotPasswordEmail });
      setForgotPasswordMessage(res.data.message || 'If an account exists, a reset link has been sent.');
    } catch (err) {
      setForgotPasswordMessage(err.response?.data?.message || 'Failed to process request');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="login-page" style={styles.page}>
        <div className="login-card" style={styles.card}>
          <div style={styles.logoSection}>
            <div style={styles.logo}>🔑</div>
            <h1 style={styles.title}>Reset Password</h1>
            <p style={styles.subtitle}>Enter your email to receive a reset link</p>
          </div>

          {forgotPasswordMessage && (
            <div style={forgotPasswordMessage.includes('sent') ? styles.success : styles.error}>
              {forgotPasswordMessage}
            </div>
          )}

          <form onSubmit={handleForgotPassword} style={styles.form}>
            <div className="form-group" style={styles.formGroup}>
              <label style={styles.label}>Email Address</label>
              <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="superadmin@example.com"
                style={styles.input}
                required
              />
            </div>

            <button
              type="submit"
              disabled={forgotPasswordLoading}
              style={styles.button}
            >
              {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <div style={styles.footer}>
            <button
              onClick={() => setShowForgotPassword(false)}
              style={styles.linkButton}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" style={styles.page}>
      <div className="login-card" style={styles.card}>
        <div style={styles.logoSection}>
          <div style={styles.logo}>⚡</div>
          <h1 style={styles.title}>Super Admin</h1>
          <p style={styles.subtitle}>Platform Administration</p>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="superadmin@example.com"
              style={styles.input}
              required
            />
          </div>

          <div className="form-group" style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={styles.input}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={styles.button}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div style={styles.footerLinks}>
          <button
            onClick={() => setShowForgotPassword(true)}
            style={styles.linkButton}
          >
            Forgot Password?
          </button>
          <span style={styles.divider}>|</span>
          <Link to="/super-admin/register" style={styles.link}>
            Register New Account
          </Link>
        </div>

        <div style={styles.footer}>
          <a href="/login" style={styles.link}>← Back to Main Login</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logo: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  success: {
    background: '#dcfce7',
    color: '#15803d',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '16px',
    transition: 'border-color 0.2s',
    outline: 'none',
  },
  button: {
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'opacity 0.2s',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
  },
  footerLinks: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
  },
  divider: {
    color: '#d1d5db',
  },
  link: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    padding: 0,
  },
};

export default SuperAdminLogin;